import React from "react";

// #region Interfaces
interface ImageViewerPolygonDimensions {
    width: number;
    height: number;
}

interface ImageViewerPolygonProps {
    polygons?: Array<Array<number>>;
    isTabletOrSmaller: boolean;
    dimensions: ImageViewerPolygonDimensions;
    imageStyle: React.CSSProperties;
    hasImage: boolean;
}
// #endregion

// #region Constants
const CSS_BASE_CLASS = "c-image-viewer";
const CSS_POLYGON_LAYER_CLASS = `${CSS_BASE_CLASS}__polygon-layer`;
const CSS_POLYGON_GROUP_CLASS = `${CSS_BASE_CLASS}__polygon-group`;
const CSS_POLYGON_CLASS = `${CSS_BASE_CLASS}__polygon`;
const PDF_PAGE_WIDTH_POINTS = 612;
const PDF_PAGE_HEIGHT_POINTS = 792;
// #endregion

// #region Helpers
// Build SVG polygon points from either normalized (0-1) or PDF point coordinates.
const buildPolygonPoints = (
    polygon: Array<number>,
    width: number,
    height: number
): string | undefined => {
    if (polygon.length < 6 || polygon.length % 2 !== 0) {
        return undefined;
    }

    const usesNormalizedCoordinates = polygon.every(
        (value) => value >= 0 && value <= 1
    );
    const xScale = usesNormalizedCoordinates
        ? width
        : width / PDF_PAGE_WIDTH_POINTS;
    const yScale = usesNormalizedCoordinates
        ? height
        : height / PDF_PAGE_HEIGHT_POINTS;

    // Scale coordinates into the image pixel space.
    const scaledValues = polygon.map((value, index) => {
        const isXCoordinate = index % 2 === 0;
        const scaledValue = isXCoordinate ? value * xScale : value * yScale;
        return scaledValue.toFixed(2);
    });

    // Convert scaled coordinate pairs into an SVG points string.
    return scaledValues
        .reduce((accumulator, value, index) => {
            if (index % 2 === 0) {
                return [...accumulator, `${value}`];
            }

            const previousPairIndex = accumulator.length - 1;
            const previousPair = accumulator[previousPairIndex] ?? "";
            accumulator[previousPairIndex] = `${previousPair},${value}`;
            return accumulator;
        }, [] as Array<string>)
        .join(" ");
};
// #endregion

// #region Component
const ImageViewerPolygon = ({
    polygons,
    isTabletOrSmaller,
    dimensions,
    imageStyle,
    hasImage,
}: ImageViewerPolygonProps) => {
    // Avoid rendering until all layout inputs are available.
    if (
        !hasImage ||
        polygons == null ||
        polygons.length === 0 ||
        dimensions.width <= 0 ||
        dimensions.height <= 0
    ) {
        return null;
    }

    return (
        <svg
            className={`${CSS_POLYGON_LAYER_CLASS} ${
                isTabletOrSmaller ? "hide-menu" : ""
            }`}
            width={dimensions.width}
            height={dimensions.height}
            viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
            preserveAspectRatio="xMidYMin meet"
            style={imageStyle}>
            {polygons.map((polygon, index) => {
                const points = buildPolygonPoints(
                    polygon,
                    dimensions.width,
                    dimensions.height
                );
                if (!points) {
                    return null;
                }

                return (
                    <g
                        className={CSS_POLYGON_GROUP_CLASS}
                        key={`polygon-group-${index}`}>
                        <polygon
                            className={CSS_POLYGON_CLASS}
                            points={points}
                        />
                    </g>
                );
            })}
        </svg>
    );
};
// #endregion

// #region Exports
export default ImageViewerPolygon;
// #endregion
